// ===========================================
// ADMIN AUTH SERVICE
// ===========================================

import api from '@/services/api';
import type { AdminUser, StoredAdminAuth } from '@/types/admin';

const STORAGE_KEY = 'tt_admin_auth';

interface LoginResponse {
  token: string;
  user: AdminUser;
}

/**
 * Admin authentication service
 * Handles admin login, logout, and token management
 */
export const adminAuthService = {
  /**
   * Login with email and password
   */
  async login(email: string, password: string): Promise<StoredAdminAuth> {
    const response = await api.post<LoginResponse>('/admin/login', { email, password });
    console.log(response.data);
    const stored: StoredAdminAuth = {
      user: response.data.user,
      token: response.data.token,
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
    return stored;
  },

  /**
   * Refresh authentication tokens
   * (Sanctum tokens are long-lived, so we just verify 'me' or re-login)
   */
  async refreshTokens(): Promise<StoredAdminAuth> {
    const existing = this.getStoredAuth();
    if (!existing) {
      throw new Error('No session');
    }

    try {
      // Validate token is still valid by fetching user
      const response = await api.get<AdminUser>('/admin/me');

      const updated: StoredAdminAuth = {
        user: response.data,
        token: existing.token
      };

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      return updated;
    } catch (error) {
      this.clearStoredAuth();
      throw error; // Let the caller know session is invalid
    }
  },

  /**
   * Get stored authentication data
   */
  getStoredAuth(): StoredAdminAuth | null {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as StoredAdminAuth;
  },

  /**
   * Clear stored authentication data (logout)
   */
  async logout(): Promise<void> {
    try {
      await api.post('/admin/logout');
    } finally {
      this.clearStoredAuth();
    }
  },

  clearStoredAuth(): void {
    localStorage.removeItem(STORAGE_KEY);
  },
};
